import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { topicList } from '@/mocks/topics';
import { newsList } from '@/mocks/news';
import PageHero from '@/components/feature/PageHero';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import { formatDate, formatViews } from '@/utils/format';

const PAGE_SIZE = 6;

export default function HotTopicsPage() {
  usePageMeta({
    title: '热门话题 - 靠逼',
    description: '靠逼热门话题频道聚焦时下讨论度最高的娱乐话题，明星动态、社交热点与影评解读一网打尽。',
    keywords: '热门话题,娱乐话题,讨论,社交热点,靠逼',
  });

  const tags = useMemo(() => {
    const set = new Set<string>();
    topicList.forEach((t) => t.tags.forEach((tag) => set.add(tag)));
    return ['全部', ...Array.from(set)];
  }, []);

  const [tag, setTag] = useState('全部');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = [...topicList].sort((a, b) => b.heat - a.heat);
    if (tag === '全部') return list;
    return list.filter((t) => t.tags.includes(tag));
  }, [tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const relatedNewsForTopic = (topicTags: string[]) =>
    newsList.find((n) => n.tags.some((t) => topicTags.includes(t)))?.id ?? newsList[0].id;

  const changeTag = (t: string) => {
    setTag(t);
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        title="热门话题"
        subtitle="围观时下讨论度最高的娱乐话题，用热度看清大家的关注焦点"
        crumbs={[{ label: '热门话题' }]}
        icon="ri-fire-line"
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="mr-1 text-sm font-medium text-foreground-700">话题标签</span>
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => changeTag(t)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                tag === t
                  ? 'bg-accent-500 font-medium text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {current.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon="ri-fire-line"
              title="暂无该标签下的话题"
              description="换一个标签看看，或者回到全部话题列表。"
              actionLabel="查看全部话题"
              onAction={() => changeTag('全部')}
            />
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-4">
            {current.map((t, idx) => (
              <Link
                key={t.id}
                to={`/detail/${relatedNewsForTopic(t.tags)}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 md:flex-row"
              >
                <div className="flex items-center gap-3 md:w-24 md:flex-col md:items-start md:justify-center">
                  <span className="font-heading text-2xl font-black text-background-300 transition-colors group-hover:text-accent-400">
                    {String((page - 1) * PAGE_SIZE + idx + 1).padStart(2, '0')}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-700">
                    <i className="ri-fire-fill" />
                    {formatViews(t.heat)}
                  </span>
                </div>

                <div className="h-40 w-full flex-none overflow-hidden rounded-md bg-background-100 md:h-24 md:w-40">
                  <img
                    src={t.cover}
                    alt={t.title}
                    title={`${t.title} - 靠逼热门话题`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {t.tags.map((tg) => (
                      <span
                        key={tg}
                        className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800"
                      >
                        {tg}
                      </span>
                    ))}
                    <span className="text-[11px] text-foreground-400">{formatDate(t.publishedAt)}</span>
                  </div>
                  <h3 className="mt-2 line-clamp-1 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-accent-600 md:text-lg">
                    {t.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{t.summary}</p>
                  <div className="mt-3 flex items-center gap-4 text-[11px] text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-voice-line" />
                      {formatViews(t.participants)} 人参与讨论
                    </span>
                    <span className="flex items-center gap-1 text-accent-600">
                      进入话题
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}